import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border-b border-gold/25 bg-transparent py-3 font-light text-parchment placeholder:text-sand/50 focus:border-gold focus:outline-none transition-colors duration-300";

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const work = e.detail;
      const title = work.title_fr || work.title_en;
      const price = work.price > 0 ? ` (${work.price} €)` : "";
      setForm((f) => ({
        ...f,
        subject: t.contact.subjects[t.contact.subjects.length - 1],
        message: `${t.boutique.enquiry_prefix}${title}${price}${t.boutique.enquiry_suffix}`,
      }));
    };
    window.addEventListener("mas:enquire", handler);
    return () => window.removeEventListener("mas:enquire", handler);
  }, [t]);

  useEffect(() => {
    const raw = sessionStorage.getItem("mas:enquire");
    if (!raw) return;
    sessionStorage.removeItem("mas:enquire");
    try {
      window.dispatchEvent(new CustomEvent("mas:enquire", { detail: JSON.parse(raw) }));
    } catch {
      /* ignore */
    }
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        subject: form.subject || t.contact.subjects[4],
      });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-gold/15 bg-ink2">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-4 py-24 sm:px-8 lg:grid-cols-2 lg:px-16 lg:py-36">
        <div>
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.contact.kicker}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-serif text-3xl font-light leading-tight text-parchment sm:text-4xl lg:text-5xl">
              {t.contact.title}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-sand sm:text-lg">
              {t.contact.sub}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">{t.contact.find_us}</p>
              <p data-testid="contact-address" className="mt-4 flex items-start gap-3 font-serif text-xl font-light leading-relaxed text-parchment/90">
                <MapPin size={18} className="mt-1.5 shrink-0 text-gold" />
                <span>
                  Abbaye Saint-Léonard
                  <br />
                  Le Bourg, 50300 Vains
                  <br />
                  Manche · Baie du Mont-Saint-Michel
                </span>
              </p>
              <a
                data-testid="contact-itinerary-link"
                href="https://www.google.com/maps/search/?api=1&query=Abbaye+Saint-L%C3%A9onard+Vains+50300"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-gold underline decoration-gold/40 underline-offset-8 transition-colors hover:text-goldlight"
              >
                {t.contact.itinerary} <ArrowUpRight size={14} />
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="space-y-8 lg:pt-16">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
                  {t.contact.name}
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
                  {t.contact.email}
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-subject" className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
                {t.contact.subject}
              </label>
              <select
                id="contact-subject"
                data-testid="contact-subject-select"
                value={form.subject}
                onChange={set("subject")}
                className={`${inputClass} cursor-pointer bg-ink2`}
              >
                {t.contact.subjects.map((s) => (
                  <option key={s} value={s} className="bg-ink2 text-parchment">
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
                {t.contact.message}
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-textarea"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                className={`${inputClass} resize-none`}
              />
            </div>
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="group relative overflow-hidden rounded-full border border-gold/60 px-10 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:text-ink disabled:opacity-50"
            >
              <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative">{sending ? t.contact.sending : t.contact.send}</span>
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
